import React, { useState, useEffect } from "react";
import axios from "axios";
import PageHeader from "../components/ui/PageHeader";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import DataTable from "../components/ui/DataTable";
import FormField from "../components/ui/FormField";
import TabBar from "../components/ui/TabBar";
import useAuth from "../hooks/useAuth";
import { FiRefreshCw } from "react-icons/fi";

const TABS = [
  { id: "list", label: "Villages" },
  { id: "add", label: "Add Village" },
];

export default function Villages() {
  const [tab, setTab] = useState("list");
  const { refreshToken } = useAuth();
  const [villages, setVillages] = useState([]);
  const [organisers, setOrganisers] = useState([]);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    organiser_id: "",
    village_name: "",
    taluka: "",
    district: "",
    pin_code: "",
  });

  /* ================= FETCH ALL VILLAGES ================= */

  const fetchVillages = async () => {
    try {
      const token = sessionStorage.getItem("maaze_token");

      const res = await axios.get(
        "https://maize.norissolutions.in/Php/public/api/villages",
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setVillages(res.data?.data || []);
    } catch (err) {
      console.log(err.response?.data);
    }
  };

  /* ================= FETCH ORGANISERS ================= */

  const fetchOrganisers = async () => {
    try {
      const token = sessionStorage.getItem("maaze_token");

      const res = await axios.get(
        "https://maize.norissolutions.in/Php/public/api/organisers",
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setOrganisers(res.data?.data || []);
    } catch (err) {
      console.log(err.response?.data);
    }
  };

  useEffect(() => {
    fetchVillages();
    fetchOrganisers();
  }, []);

  /* ================= SAVE (POST / PUT) ================= */

  const handleSubmit = async () => {
    try {
      const token = sessionStorage.getItem("maaze_token");

      const payload = {
        ...form,
        organiser_id: Number(form.organiser_id),
        pin_code: Number(form.pin_code),
      };

      if (editingId) {
        await axios.put(
          `https://maize.norissolutions.in/Php/public/api/villages/${editingId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } else {
        await axios.post(
          "https://maize.norissolutions.in/Php/public/api/villages",
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }

      setEditingId(null);
      setForm({
        organiser_id: "",
        village_name: "",
        taluka: "",
        district: "",
        pin_code: "",
      });

      fetchVillages();
      setTab("list");
    } catch (err) {
      console.log(err.response?.data);
    }
  };

  /* ================= EDIT ================= */

  const handleEdit = (row) => {
    setForm({
      organiser_id: row.organiser_id,
      village_name: row.village_name,
      taluka: row.taluka,
      district: row.district,
      pin_code: row.pin_code,
    });

    setEditingId(row.village_id);
    setTab("add");
  };

  /* ================= DELETE ================= */

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this village?")) return;

    try {
      const token = sessionStorage.getItem("maaze_token");

      await axios.delete(
        `https://maize.norissolutions.in/Php/public/api/villages/${id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      fetchVillages();
    } catch (err) {
      console.log(err.response?.data);
    }
  };

  /* ================= TABLE COLUMNS ================= */

  const COLUMNS = [
    {
      key: "village_id",
      label: "ID",
      render: (v) => (
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>
          {v}
        </span>
      ),
    },
    {
      key: "village_name",
      label: "Village",
      render: (v) => <strong>🏘 {v}</strong>,
    },
    { key: "organiser_name", label: "Organiser" },
    { key: "taluka", label: "Taluka" },
    { key: "district", label: "District" },
    {
      key: "_actions",
      label: "Actions",
      render: (v, row) => (
        <>
          <Button variant="ghost" size="sm" onClick={() => handleEdit(row)}>
            ✏️
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(row.village_id)}
          >
            🗑
          </Button>
        </>
      ),
    },
  ];

  /* ================= UI ================= */

  return (
    <div>
      <PageHeader
        title="Villages"
        subtitle="Manage village records under organiser zones"
      >
        <FiRefreshCw className="page-refresh" onClick={refreshToken} />
      </PageHeader>

      <TabBar tabs={TABS} active={tab} onChange={setTab} />

      {tab === "list" && (
        <Card
          title="All Villages"
          action={
            <>
              <Button size="sm" onClick={() => setTab("add")}>
                + Add Village
              </Button>
            </>
          }
          noPad
        >
          <DataTable columns={COLUMNS} rows={villages} />
        </Card>
      )}

      {tab === "add" && (
        <Card title="Add New Village">
          <div className="form-grid">
            <FormField
              label="Village Name"
              type="text"
              value={form.village_name}
              onChange={(e) =>
                setForm({ ...form, village_name: e.target.value })
              }
            />

            <FormField
              label="Organiser"
              type="select"
              options={organisers.map((o) => ({
                label: o.organiser_name,
                value: o.organiser_id,
              }))}
              value={form.organiser_id}
              onChange={(e) =>
                setForm({ ...form, organiser_id: e.target.value })
              }
            />

            <FormField
              label="District"
              type="text"
              value={form.district}
              onChange={(e) => setForm({ ...form, district: e.target.value })}
            />

            <FormField
              label="Taluka"
              type="text"
              value={form.taluka}
              onChange={(e) => setForm({ ...form, taluka: e.target.value })}
            />

            <FormField
              label="Pincode"
              type="text"
              value={form.pin_code}
              onChange={(e) => setForm({ ...form, pin_code: e.target.value })}
            />
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <Button onClick={handleSubmit}>
              {editingId ? "Update Village" : "Save Village"}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setEditingId(null);
                setForm({
                  organiser_id: "",
                  village_name: "",
                  taluka: "",
                  district: "",
                  pin_code: "",
                });
                setTab("list");
              }}
            >
              Cancel
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
